/**
 * Canonicalizes trailing slashes in the URL (`/support/tickets/` →
 * `/support/tickets`).
 *
 * React Router already MATCHES a trailing-slash path, so this is not about
 * rendering the right page — it is about the address bar agreeing with the rest
 * of the app, so a copied link, a bookmark, or an `entityUrl` comparison sees
 * one canonical form.
 *
 * On the web the CloudFront SPA router 301s these at the edge before the app
 * ever loads (infrastructure/pulumi/components/spa_router_function.js). This
 * handler covers the surfaces that never touch CloudFront: local dev (Vite
 * serves index.html for any path), and the Capacitor/Tauri shells, which load
 * the SPA from a local origin and can be deep-linked with a trailing slash.
 */
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export function TrailingSlashRedirect() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // The site root is already canonical.
    if (location.pathname === '/' || !location.pathname.endsWith('/')) {
      return;
    }

    const canonical = location.pathname.replace(/\/+$/, '');
    // "//evil.com" is a protocol-relative URL and "/\evil.com" is normalized to
    // one — pushing either through history would resolve off-origin (and throw
    // a SecurityError). Leave those to the 404 route, same as the edge function.
    if (/^\/[/\\]/.test(canonical)) {
      return;
    }

    navigate(
      {
        pathname: canonical === '' ? '/' : canonical,
        search: location.search,
        hash: location.hash,
      },
      { replace: true }
    );
  }, [location.pathname, location.search, location.hash, navigate]);

  return null;
}
